import { OWNER_STAFF_ID } from './salonDays'
import { performerOf, type Viewer } from './staffAccess'
import type { AdminUser } from '../types/adminRequests'
import type { Grants, Limits, Permission, Role, RoleId, Scope, SensitivityClass } from '../types/team'

/**
 * De bevoegdhedenlijst (docs/RECHTEN-EN-SCHAAL.md §4–§5): één bron voor menu, routes, schermdelen
 * en de demo-server. De server spiegelt hem; tests/vectors/permissions.json legt de sjablonen vast
 * zodat site en server aantoonbaar hetzelfde toestaan. Puur en getest.
 *
 * De code vraagt nooit "is dit de eigenaar?" maar `can(access, 'kassa.void', bon)`.
 */

/** Elke bevoegdheid met zijn gevoeligheidsklasse (§4.1). */
export const PERMISSION_CLASS = {
  'agenda.view': 'B',
  'agenda.view.busy': 'A',
  'agenda.plan': 'B',
  'agenda.requests': 'B',
  'agenda.complete': 'C',
  'agenda.override': 'B',
  'agenda.blocks': 'A',
  'agenda.roster': 'A',
  'customers.view': 'B',
  'customers.edit': 'B',
  'customers.health': 'C',
  'customers.credit': 'D',
  'customers.merge': 'B',
  'customers.export': 'E',
  'customers.erase': 'E',
  'kassa.sell': 'B',
  'kassa.discount': 'D',
  'kassa.void': 'D',
  'kassa.refund': 'D',
  'kassa.close': 'D',
  'kassa.totals': 'D',
  'orders.view': 'B',
  'orders.fulfil': 'A',
  'orders.service': 'B',
  'orders.refund': 'D',
  'orders.cancel': 'D',
  'subscriptions.manage': 'B',
  'revenue.view': 'D',
  'revenue.export': 'D',
  'vouchers.view': 'B',
  'vouchers.code': 'D',
  'vouchers.issue': 'D',
  'vouchers.correct': 'D',
  'liabilities.view': 'D',
  'hours.own': 'A',
  'hours.all': 'D',
  'finance.margin': 'D',
  'stock.use': 'A',
  'stock.receive': 'A',
  'stock.cost': 'D',
  'stock.transfer': 'A',
  'catalog.edit': 'A',
  'catalog.price': 'D',
  'content.edit': 'A',
  'marketing.newsletter': 'B',
  'marketing.social': 'A',
  'insights.view': 'A',
  'seo.edit': 'A',
  'team.manage': 'E',
  'settings.edit': 'E',
  'integrations.manage': 'E',
  'audit.view': 'E',
} as const satisfies Record<Permission, SensitivityClass>

export const ALL_PERMISSIONS = Object.keys(PERMISSION_CLASS) as Permission[]

const NO_LIMITS: Limits = { discountMaxPercent: null, refundMaxCents: null, voucherMaxCents: null, creditMaxCents: null }

/**
 * De sjablonen (§5). Behandelaar (zzp) = precies wat de collega vandaag mag (kolom "Nu" in §4.2).
 * Afwijkingen van die kolom, zodat er niets verandert: blokkades zetten en eigen afwezigheid melden
 * kan ze nu niet (alleen een blokkade bij haar eigen afspraken opheffen).
 */
export const ROLES: Record<RoleId, Role> = {
  eigenaar: {
    id: 'eigenaar',
    name: 'Eigenaar',
    description: 'Alles, ook team, instellingen en het logboek.',
    grants: Object.fromEntries(ALL_PERMISSIONS.map((p) => [p, 'all'])) as Grants,
    limits: NO_LIMITS,
    treats: true,
  },
  'behandelaar-zzp': {
    id: 'behandelaar-zzp',
    name: 'Behandelaar (zzp)',
    description: 'Eigen afspraken plannen, afronden en afrekenen; de beperkte klantkaart. Geen omzet, export of instellingen.',
    grants: {
      'agenda.view': 'own',
      'agenda.plan': 'own',
      'agenda.requests': 'own',
      'agenda.complete': 'own',
      'agenda.blocks': 'own',
      'customers.view': 'own',
      'customers.edit': 'own',
      'customers.health': 'own',
      'kassa.sell': 'own',
      'kassa.discount': 'all',
      'kassa.void': 'own',
      'kassa.close': 'all',
    },
    // Korting: alleen de merkregel (beslissing §11.2 staat open). Terugbetalen, bonnen en tegoed: niet.
    limits: { discountMaxPercent: null, refundMaxCents: 0, voucherMaxCents: 0, creditMaxCents: 0 },
    treats: true,
  },
  // Website, journal, social, ontwerpen, nieuwsbrief en Inzicht (zonder klantnamen en zonder omzet
  // van salon of webshop; omzet per bron en per nieuwsbrief wel, om campagnes te beoordelen).
  // Geen klantkaarten, geen e-mailadressen van lezers, geen geld, geen instellingen of team.
  marketing: {
    id: 'marketing',
    name: 'Marketing',
    description: 'Teksten, journal, social, ontwerpen, nieuwsbrief en Inzicht. Geen klanten, omzet, kassa of instellingen.',
    grants: {
      'content.edit': 'all',
      'marketing.social': 'all',
      'marketing.newsletter': 'all',
      'insights.view': 'all',
      'seo.edit': 'all',
    },
    limits: { discountMaxPercent: 0, refundMaxCents: 0, voucherMaxCents: 0, creditMaxCents: 0 },
    treats: false,
  },
  boekhouder: {
    id: 'boekhouder',
    name: 'Boekhouder',
    description: 'Alleen lezen: omzet, marge en kosten, export, openstaande bonnen en kuren, uren. Geen klantgegevens.',
    grants: {
      'revenue.view': 'all',
      'revenue.export': 'all',
      'liabilities.view': 'all',
      'vouchers.view': 'all',
      'hours.all': 'all',
      'finance.margin': 'all',
    },
    limits: { discountMaxPercent: 0, refundMaxCents: 0, voucherMaxCents: 0, creditMaxCents: 0 },
    treats: false,
  },
}

/** Beslissing §11.5 (boekhouder met eigen login) staat open: tot dan niet te kiezen. */
export const BOOKKEEPER_ENABLED = false

/** De rollen die Sarah in Team en toegang kan kiezen. */
export const selectableRoles = (): Role[] =>
  [ROLES.eigenaar, ROLES['behandelaar-zzp'], ROLES.marketing, ...(BOOKKEEPER_ENABLED ? [ROLES.boekhouder] : [])]

export const isRoleId = (v: unknown): v is RoleId => typeof v === 'string' && v in ROLES

const wider = (a: Scope | undefined, b: Scope | undefined): Scope | undefined => (a === 'all' || b === 'all' ? 'all' : (a ?? b))
const higher = (a: number | null, b: number | null): number | null => (a === null || b === null ? null : Math.max(a, b))

/**
 * Bevoegdheden van een account met deze rollen: ze tellen op, het breedste bereik wint. Klasse C
 * (gezondheid) komt alleen van een rol die behandelt (§5 "Combineren").
 */
export function permissionsFor(roleIds: readonly RoleId[], roles: Record<string, Role> = ROLES): Grants {
  const out: Grants = {}
  for (const id of roleIds) {
    const role = roles[id]
    if (!role) continue
    for (const [p, scope] of Object.entries(role.grants) as [Permission, Scope][]) {
      if (PERMISSION_CLASS[p] === 'C' && !role.treats) continue
      out[p] = wider(out[p], scope)
    }
  }
  return out
}

/** Grenzen van een account: de hoogste per soort. Geen rol = alles op 0. */
export function limitsFor(roleIds: readonly RoleId[], roles: Record<string, Role> = ROLES): Limits {
  const list = roleIds.map((id) => roles[id]).filter((r): r is Role => Boolean(r))
  if (list.length === 0) return { discountMaxPercent: 0, refundMaxCents: 0, voucherMaxCents: 0, creditMaxCents: 0 }
  return list.slice(1).reduce<Limits>(
    (acc, r) => ({
      discountMaxPercent: higher(acc.discountMaxPercent, r.limits.discountMaxPercent),
      refundMaxCents: higher(acc.refundMaxCents, r.limits.refundMaxCents),
      voucherMaxCents: higher(acc.voucherMaxCents, r.limits.voucherMaxCents),
      creditMaxCents: higher(acc.creditMaxCents, r.limits.creditMaxCents),
    }),
    { ...list[0].limits },
  )
}

/** Wat het ingelogde account mag, plus bij welke persoon het hoort (voor "eigen"). */
export type Access = { grants: Grants; limits: Limits; staffId: string }

export const scopeOf = (a: Access, p: Permission): Scope | null => a.grants[p] ?? null

/**
 * Mag dit? Zonder rij: heeft het account deze bevoegdheid (eigen of alles). Met een rij: bij `own`
 * alleen als de rij bij haar persoon hoort (een rij zonder `staffId` is van de eigenaar).
 */
export function can(a: Access, p: Permission, row?: { staffId?: string }): boolean {
  const scope = scopeOf(a, p)
  if (!scope) return false
  if (scope === 'all' || !row) return true
  return Boolean(a.staffId) && performerOf(row.staffId) === a.staffId
}

/** Mag dit voor álles (niet alleen eigen rijen)? */
export const canAll = (a: Access, p: Permission) => scopeOf(a, p) === 'all'

/**
 * Een kijker voor de bestaande regels (`ownOnly`, `kassaDayFor`, `bookingProblem`): "alles" als het
 * account deze bevoegdheid voor alles heeft. Zo noemt elke plek de bevoegdheid die hij bedoelt.
 */
export const viewerFor = (a: Access, p: Permission): Viewer => ({ isOwner: canAll(a, p), staffId: a.staffId })

/** Rollen uit de sessie. Een oudere server stuurt alleen `role`: eigenaar of zzp. */
export function rolesOf(user: Pick<AdminUser, 'role' | 'roles'>): RoleId[] {
  const known = (user.roles ?? []).filter(isRoleId)
  if (known.length > 0) return known
  return [user.role === 'staff' ? 'behandelaar-zzp' : 'eigenaar']
}

/** Wat deze sessie mag. De server stuurt `permissions` en `limits`; anders afgeleid van de rollen. */
export function accessOf(user: Pick<AdminUser, 'role' | 'roles' | 'permissions' | 'limits' | 'staffId' | 'personId'>): Access {
  const roles = rolesOf(user)
  const grants = user.permissions ?? permissionsFor(roles)
  const limits = user.limits ?? limitsFor(roles)
  const person = user.personId ?? user.staffId
  // Wie alles in de agenda ziet zonder gekoppelde persoon, is de eigenaar (oudere server).
  const staffId = person || (grants['agenda.view'] === 'all' ? OWNER_STAFF_ID : '')
  return { grants, limits, staffId }
}

/** Een account voor de controle op "laatste eigenaar". */
type OwnerCheck = { accountId: string; roles: readonly RoleId[]; active: boolean }

/**
 * Er blijft altijd minstens één actief account met de rol Eigenaar (§3.2). `null` = mag.
 * `change`: nieuwe rollen, of `stop` (account inactief).
 */
export function lastOwnerProblem(accounts: readonly OwnerCheck[], accountId: string, change: { roles?: readonly RoleId[]; stop?: boolean }): string | null {
  const owners = accounts.filter((x) => x.active && x.roles.includes('eigenaar'))
  const isOwner = owners.some((x) => x.accountId === accountId)
  if (!isOwner || owners.length > 1) return null
  const losesRole = change.stop || (change.roles && !change.roles.includes('eigenaar'))
  return losesRole ? 'Er moet altijd iemand eigenaar zijn. Maak eerst iemand anders eigenaar.' : null
}
